/**
 * Initialisation générique des Swipers personnalisés
 * Architecture: 1 section = 1 instance Swiper isolée
 * Optimisé pour LCP (Largest Contentful Paint) et performances
 * MODE STRICT : Pas de fallback, erreur si configuration manquante
 * 
 * ==================================================
 * ATTRIBUTS OBLIGATOIRES (erreur si manquants)
 * ==================================================
 * - data-swiper="uniqueId" : Identifiant unique du swiper
 * - data-slides-per-view="2" ou "auto" : Nombre de slides visibles (desktop) ou "auto" pour largeur basée sur contenu CSS
 * 
 * ==================================================
 * ATTRIBUTS OPTIONNELS AVEC DEFAULTS
 * ==================================================
 * - data-space-between="20" : Espacement entre les slides (px, défaut: 0)
 * 
 * ==================================================
 * ATTRIBUTS OPTIONNELS (avec defaults explicites)
 * ==================================================
 * - data-slides-per-view-mobile="1" : Slides visibles mobile (défaut: desktop)
 * - data-slides-per-view-tablet="2" : Slides visibles tablette (défaut: desktop)
 * - data-space-between-mobile="10" : Espacement mobile (défaut: desktop)
 * - data-space-between-tablet="15" : Espacement tablette (défaut: desktop)
 * - data-autoplay="true" : Activer l'autoplay (défaut: false)
 * - data-autoplay-delay="3000" : Délai autoplay (défaut: 3000ms)
 * - data-pagination="true" : Activer la pagination (défaut: false)
 * - data-scrollbar="true" : Activer la scrollbar (défaut: false)
 * - data-centered-slides="true" : Centrer les slides (défaut: false)
 * 
 * Note: Pour afficher partiellement la slide suivante, utilisez des valeurs décimales
 * dans data-slides-per-view (ex: "3.2" = 3 slides complètes + 20% de la suivante)
 * 
 * ==================================================
 * BOUTONS DE NAVIGATION
 * ==================================================
 * Par défaut, cherche: .swiper-button-prev-{uniqueId} et .swiper-button-next-{uniqueId}
 * Personnalisable via: data-swiper-prev=".custom-prev" et data-swiper-next=".custom-next"
 * 
 * ==================================================
 * SCOPE STRICT
 * ==================================================
 * Les boutons doivent être dans la même section root que le swiper:
 * - .product__block
 * - [id^="Block--"]
 */
(function() {
  'use strict';
  
  // Mode debug : actif automatiquement en mode design Shopify
  // Détecte automatiquement si on est dans l'éditeur de thème Shopify
  const DEBUG = document.documentElement.classList.contains('shopify-design-mode') || 
                window.Shopify?.designMode === true ||
                document.body.classList.contains('shopify-design-mode');
  
  // Logging conditionnel (seulement si DEBUG = true)
  const log = DEBUG ? console.log.bind(console) : function() {};
  const warn = DEBUG ? console.warn.bind(console) : function() {};
  
  // Erreurs critiques : TOUJOURS affichées (même si DEBUG = false)
  // Car les erreurs de configuration doivent être visibles en production aussi
  const error = console.error.bind(console);
  
  // Sélecteur par défaut pour les swipers
  const DEFAULT_SWIPER_SELECTOR = '[data-swiper]';
  
  // Breakpoints Swiper (centralisés pour maintenance)
  const BREAKPOINT_MOBILE = 0;
  const BREAKPOINT_TABLET = 767;
  const BREAKPOINT_DESKTOP = 1024;
  
  // Map des instances Swiper par section (isolation stricte)
  const swiperInstances = new Map();
  
  /**
   * Trouve le scope root de la section contenant le swiper
   * @param {HTMLElement} swiperEl - L'élément swiper
   * @returns {HTMLElement|null} - Le conteneur section root ou null
   */
  function getSectionRoot(swiperEl) {
    // Chercher dans l'ordre de priorité :
    // 1. .product__block (conteneur Shopify standard)
    // 2. Parent direct avec ID (Block--xxx)
    // 3. Parent direct (fallback)
    return swiperEl.closest('.product__block') || 
           swiperEl.closest('[id^="Block--"]') ||
           swiperEl.parentElement;
  }
  
  /**
   * Trouve les boutons de navigation dans le scope de la section
   * @param {HTMLElement} sectionRoot - Le conteneur section root
   * @param {HTMLElement} swiperEl - L'élément swiper
   * @param {string} uniqueId - L'ID unique du swiper
   * @returns {Object|null} - {prev, next} ou null si non trouvés
   */
  function getNavigationButtons(sectionRoot, swiperEl, uniqueId) {
    if (!sectionRoot) {
      return null;
    }
    
    // Option 1: Sélecteurs personnalisés via data-attributes
    const prevSelector = swiperEl.dataset.swiperPrev;
    const nextSelector = swiperEl.dataset.swiperNext;
    
    let prevButton = null;
    let nextButton = null;
    
    if (prevSelector && nextSelector) {
      // Utiliser les sélecteurs personnalisés (scope strict: sectionRoot uniquement)
      prevButton = sectionRoot.querySelector(prevSelector);
      nextButton = sectionRoot.querySelector(nextSelector);
    } else if (uniqueId) {
      // Option 2: Pattern par défaut avec uniqueId (scope strict: sectionRoot uniquement)
      prevButton = sectionRoot.querySelector(`.swiper-button-prev-${uniqueId}`);
      nextButton = sectionRoot.querySelector(`.swiper-button-next-${uniqueId}`);
    }
    
    if (!prevButton || !nextButton) {
      if (DEBUG) {
        warn(`⚠️ Boutons non trouvés pour swiper ${uniqueId} dans le scope de la section`);
      }
      return null;
    }
    
    return { prev: prevButton, next: nextButton };
  }
  
  /**
   * Valide et lit la configuration depuis les data-attributes du swiper
   * MODE STRICT : Aucun fallback, erreur si config manquante
   * @param {HTMLElement} swiperEl - L'élément swiper
   * @returns {Object|null} - Configuration Swiper ou null si invalide
   */
  function readConfig(swiperEl) {
    const uniqueId = swiperEl.getAttribute('data-swiper');
    const errors = [];
    
    // Attributs OBLIGATOIRES (pas de fallback)
    const slidesPerViewStr = swiperEl.dataset.slidesPerView;
    
    if (!slidesPerViewStr || slidesPerViewStr.trim() === '') {
      errors.push('data-slides-per-view est OBLIGATOIRE');
    }
    
    if (errors.length > 0) {
      error(`❌ Swiper ${uniqueId} : Configuration invalide.`, errors);
      error(`   Élément:`, swiperEl);
      error(`   Attributs manquants:`, errors);
      return null;
    }
    
    // Parse des valeurs obligatoires
    // Support des valeurs décimales (ex: 3.2) et "auto" pour slidesPerView
    let slidesPerView;
    if (slidesPerViewStr === 'auto') {
      slidesPerView = 'auto';
    } else {
      slidesPerView = parseFloat(slidesPerViewStr);
      if (isNaN(slidesPerView) || slidesPerView <= 0) {
        errors.push(`data-slides-per-view invalide: "${slidesPerViewStr}"`);
      }
    }
    
    if (errors.length > 0) {
      error(`❌ Swiper ${uniqueId} : Valeurs invalides.`, errors);
      return null;
    }
    
    // Attributs OPTIONNELS avec defaults explicites (documentés dans le header)
    const centeredSlides = swiperEl.dataset.centeredSlides === 'true' || swiperEl.dataset.centeredSlides === '1';
    const autoplay = swiperEl.dataset.autoplay === 'true' || swiperEl.dataset.autoplay === '1';
    const pagination = swiperEl.dataset.pagination === 'true' || swiperEl.dataset.pagination === '1';
    const scrollbar = swiperEl.dataset.scrollbar === 'true' || swiperEl.dataset.scrollbar === '1';
    // Direction verticale pour navigation type YouTube Shorts
    const direction = swiperEl.dataset.direction === 'vertical' ? 'vertical' : 'horizontal';
    
    // Defaults explicites pour attributs optionnels (documentés)
    
    // autoplayDelay: default 3000ms (standard Swiper)
    const autoplayDelayStr = swiperEl.dataset.autoplayDelay;
    const autoplayDelay = autoplayDelayStr ? parseInt(autoplayDelayStr, 10) : 3000;
    
    // ==================================================
    // ESPACEMENT GLOBAL (source de vérité)
    // ==================================================
    const spaceBetweenGlobalStr = swiperEl.dataset.spaceBetween;
    const spaceBetweenGlobal = spaceBetweenGlobalStr
      ? parseInt(spaceBetweenGlobalStr, 10)
      : 0; // default explicite
    
    // Validation stricte uniquement si présent
    if (spaceBetweenGlobalStr && (isNaN(spaceBetweenGlobal) || spaceBetweenGlobal < 0)) {
      errors.push(`data-space-between invalide: "${spaceBetweenGlobalStr}"`);
    }
    
    // ==================================================
    // BREAKPOINTS : slides-per-view (validation stricte)
    // ==================================================
    const slidesPerViewMobileStr = swiperEl.dataset.slidesPerViewMobile;
    const slidesPerViewTabletStr = swiperEl.dataset.slidesPerViewTablet;
    
    // Mobile : si défini, doit être valide, sinon utilise desktop
    // Support des valeurs décimales (ex: 2.5) et "auto"
    let slidesPerViewMobile = slidesPerView;
    if (slidesPerViewMobileStr) {
      if (slidesPerViewMobileStr === 'auto') {
        slidesPerViewMobile = 'auto';
      } else {
        const parsed = parseFloat(slidesPerViewMobileStr);
        if (isNaN(parsed) || parsed <= 0) {
          errors.push(`data-slides-per-view-mobile invalide: "${slidesPerViewMobileStr}"`);
        } else {
          slidesPerViewMobile = parsed;
        }
      }
    }
    
    // Tablet : si défini, doit être valide, sinon utilise desktop
    // Support des valeurs décimales (ex: 2.5) et "auto"
    let slidesPerViewTablet = slidesPerView;
    if (slidesPerViewTabletStr) {
      if (slidesPerViewTabletStr === 'auto') {
        slidesPerViewTablet = 'auto';
      } else {
        const parsed = parseFloat(slidesPerViewTabletStr);
        if (isNaN(parsed) || parsed <= 0) {
          errors.push(`data-slides-per-view-tablet invalide: "${slidesPerViewTabletStr}"`);
        } else {
          slidesPerViewTablet = parsed;
        }
      }
    }
    
    // ==================================================
    // OVERRIDES ESPACEMENT PAR BREAKPOINT (optionnels)
    // ==================================================
    const spaceBetweenMobileStr = swiperEl.dataset.spaceBetweenMobile;
    const spaceBetweenTabletStr = swiperEl.dataset.spaceBetweenTablet;
    
    // Mobile : override seulement si présent, sinon hérite du global
    let spaceBetweenMobile = spaceBetweenGlobal;
    if (spaceBetweenMobileStr) {
      const parsed = parseInt(spaceBetweenMobileStr, 10);
      if (isNaN(parsed) || parsed < 0) {
        errors.push(`data-space-between-mobile invalide: "${spaceBetweenMobileStr}"`);
      } else {
        spaceBetweenMobile = parsed;
      }
    }
    
    // Tablet : override seulement si présent, sinon hérite du global
    let spaceBetweenTablet = spaceBetweenGlobal;
    if (spaceBetweenTabletStr) {
      const parsed = parseInt(spaceBetweenTabletStr, 10);
      if (isNaN(parsed) || parsed < 0) {
        errors.push(`data-space-between-tablet invalide: "${spaceBetweenTabletStr}"`);
      } else {
        spaceBetweenTablet = parsed;
      }
    }
    
    if (errors.length > 0) {
      error(`❌ Swiper ${uniqueId} : Erreurs de validation.`, errors);
      return null;
    }
    
    // Config Swiper finale (logique et lisible)
    // Une seule source de vérité pour spaceBetweenGlobal
    // Overrides seulement si nécessaire
    const config = {
      direction: direction, // horizontal (défaut) ou vertical
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetweenGlobal,
      speed: 300, // Valeur standard Swiper (fixe, non configurable)
      centeredSlides: centeredSlides,
      breakpoints: {
        [BREAKPOINT_MOBILE]: {
          slidesPerView: slidesPerViewMobile,
          spaceBetween: spaceBetweenMobile
        },
        [BREAKPOINT_TABLET]: {
          slidesPerView: slidesPerViewTablet,
          spaceBetween: spaceBetweenTablet
        },
        [BREAKPOINT_DESKTOP]: {
          slidesPerView: slidesPerView,
          spaceBetween: spaceBetweenGlobal
        }
      }
    };
    
    if (autoplay) {
      config.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
      };
    }
    
    if (pagination) {
      const paginationEl = swiperEl.querySelector('.swiper-pagination');
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }
    }
    
    if (scrollbar) {
      const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');
      if (scrollbarEl) {
        config.scrollbar = {
          el: scrollbarEl,
          draggable: true,
        };
      }
    }
    
    return config;
  }
  
  /**
   * Détruit proprement une instance Swiper
   * @param {HTMLElement} swiperEl - L'élément swiper
   */
  function destroySwiper(swiperEl) {
    if (!swiperEl) {
      return;
    }
    
    const instance = swiperInstances.get(swiperEl);
    if (instance && instance.destroy) {
      try {
        instance.destroy(true, true); // destroy: true, cleanStyles: true
        log('🗑️ Swiper détruit:', swiperEl.getAttribute('data-swiper'));
      } catch (error) {
        warn('⚠️ Erreur lors de la destruction:', error);
      }
    }
    
    // Nettoyer la map
    swiperInstances.delete(swiperEl);
    
    // Retirer le flag d'initialisation
    swiperEl.removeAttribute('data-swiper-initialized');
  }
  
  /**
   * Initialise un swiper individuel dans son scope strict
   * @param {HTMLElement} swiperEl - L'élément conteneur du swiper
   * @returns {boolean} - true si initialisé avec succès, false sinon
   */
  function initSwiper(swiperEl) {
    if (typeof Swiper === 'undefined') {
      if (DEBUG) warn('⚠️ Swiper.js non chargé');
      return false;
    }
    
    // Vérifier si déjà initialisé
    if (swiperEl.dataset.swiperInitialized === 'true') {
      return false;
    }
    
    // Récupérer l'ID unique
    const uniqueId = swiperEl.getAttribute('data-swiper');
    if (!uniqueId) {
      if (DEBUG) warn('⚠️ Swiper sans uniqueId, ignoré');
      return false;
    }
    
    // Trouver le scope root de la section (isolation stricte)
    const sectionRoot = getSectionRoot(swiperEl);
    if (!sectionRoot) {
      if (DEBUG) warn('⚠️ Section root non trouvée pour swiper:', uniqueId);
      return false;
    }
    
    // Trouver les boutons dans le scope strict de la section
    const navigation = getNavigationButtons(sectionRoot, swiperEl, uniqueId);
    if (!navigation) {
      return false;
    }
    
    // Lire la configuration depuis les data-attributes (MODE STRICT : pas de fallback)
    const config = readConfig(swiperEl);
    
    // Si config invalide, abort immédiatement (pas de fallback)
    if (!config) {
      error(`❌ Swiper ${uniqueId} : Initialisation ABORTÉE - Configuration invalide`);
      return false;
    }
    
    // Ajouter la navigation
    config.navigation = {
      nextEl: navigation.next,
      prevEl: navigation.prev,
    };
    
    try {
      // Créer l'instance Swiper
      const swiperInstance = new Swiper(swiperEl, config);
      
      // Stocker l'instance dans la map (isolation par élément)
      swiperInstances.set(swiperEl, swiperInstance);
      
      // Marquer comme initialisé
      swiperEl.dataset.swiperInitialized = 'true';
      
      if (DEBUG) {
        log('✅ Swiper initialisé:', uniqueId, 'dans section:', sectionRoot.id || sectionRoot.className);
      }
      
      return true;
    } catch (err) {
      error('❌ Erreur lors de l\'initialisation du Swiper:', uniqueId, err);
      return false;
    }
  }
  
  /**
   * Scanne le DOM pour trouver et initialiser tous les swipers non initialisés
   * @param {HTMLElement} container - Conteneur à scanner (optionnel, défaut: document)
   */
  function scanSections(container = document) {
    if (typeof Swiper === 'undefined') {
      return;
    }
    
    const swiperContainers = container.querySelectorAll(`${DEFAULT_SWIPER_SELECTOR}:not([data-swiper-initialized="true"])`);
    
    if (!swiperContainers.length) {
      return;
    }
    
    swiperContainers.forEach((swiperEl) => {
      initSwiper(swiperEl);
    });
    
    // Vérifier si on peut disconnect l'observer après le scan
    checkAndDisconnectObserver();
  }
  
  /**
   * Initialise immédiatement les swipers avec priorité "immediate"
   * Utilisé pour éviter les flashes visuels sur les swipers critiques
   * @returns {number} - Nombre de swipers initialisés
   */
  function initPrioritySwipers() {
    if (typeof Swiper === 'undefined') {
      if (DEBUG) warn('⚠️ Swiper.js non chargé pour l\'initialisation prioritaire');
      return 0;
    }
    
    const prioritySwipers = document.querySelectorAll(
      `${DEFAULT_SWIPER_SELECTOR}[data-swiper-priority="immediate"]:not([data-swiper-initialized="true"])`
    );
    
    if (!prioritySwipers.length) {
      return 0;
    }
    
    let initializedCount = 0;
    prioritySwipers.forEach((swiperEl) => {
      if (initSwiper(swiperEl)) {
        initializedCount++;
      }
    });
    
    if (DEBUG && initializedCount > 0) {
      log(`⚡ ${initializedCount} swiper(s) prioritaire(s) initialisé(s) immédiatement`);
    }
    
    return initializedCount;
  }
  
  /**
   * Initialisation différée après le LCP
   * Utilise requestIdleCallback si disponible, sinon setTimeout
   */
  function initWhenIdle() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        scanSections();
        // Vérifier après scan initial si on peut disconnect
        checkAndDisconnectObserver();
      }, { timeout: 2000 });
    } else {
      setTimeout(() => {
        scanSections();
        // Vérifier après scan initial si on peut disconnect
        checkAndDisconnectObserver();
      }, 200);
    }
  }
  
  /**
   * MutationObserver optimisé - ne s'active que si nécessaire
   * Détecte l'ajout de nouveaux swipers au DOM
   * Se désactive automatiquement quand tous les swipers sont initialisés
   */
  let swiperObserver = null;
  let observerActive = false;
  
  /**
   * Vérifie si tous les swipers sont initialisés et disconnect l'observer si nécessaire
   * Optimisation : arrête l'observer pour économiser les ressources
   */
  function checkAndDisconnectObserver() {
    if (!observerActive || !swiperObserver) {
      return;
    }
    
    const hasUninitializedSwipers = document.querySelectorAll(
      `${DEFAULT_SWIPER_SELECTOR}:not([data-swiper-initialized="true"])`
    ).length > 0;
    
    if (!hasUninitializedSwipers) {
      swiperObserver.disconnect();
      observerActive = false;
      if (DEBUG) {
        log('✅ Tous les swipers initialisés, observer désactivé');
      }
    }
  }
  
  function setupObserver() {
    if (observerActive || !document.body) {
      return;
    }
    
    // Ne créer l'observer que si on détecte un swiper non initialisé
    const hasUninitializedSwipers = document.querySelectorAll(`${DEFAULT_SWIPER_SELECTOR}:not([data-swiper-initialized="true"])`).length > 0;
    
    if (!hasUninitializedSwipers) {
      return;
    }
    
    observerActive = true;
    swiperObserver = new MutationObserver((mutations) => {
      // Utiliser requestIdleCallback pour éviter de bloquer le rendu
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          const hasNewSwiper = Array.from(mutations).some(mutation => {
            return Array.from(mutation.addedNodes).some(node => {
              if (node.nodeType === 1) { // Element node
                return node.querySelector && (
                  node.querySelector(DEFAULT_SWIPER_SELECTOR) || 
                  (node.matches && node.matches(DEFAULT_SWIPER_SELECTOR))
                );
              }
              return false;
            });
          });
          
          if (hasNewSwiper) {
            scanSections();
            // Vérifier après scan si on peut disconnect
            checkAndDisconnectObserver();
          }
        }, { timeout: 500 });
      } else {
        setTimeout(() => {
          const hasNewSwiper = Array.from(mutations).some(mutation => {
            return Array.from(mutation.addedNodes).some(node => {
              if (node.nodeType === 1) {
                return node.querySelector && (
                  node.querySelector(DEFAULT_SWIPER_SELECTOR) || 
                  (node.matches && node.matches(DEFAULT_SWIPER_SELECTOR))
                );
              }
              return false;
            });
          });
          
          if (hasNewSwiper) {
            scanSections();
            // Vérifier après scan si on peut disconnect
            checkAndDisconnectObserver();
          }
        }, 100);
      }
    });
    
    swiperObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  /**
   * Gère le rechargement d'une section Shopify
   * @param {Event} event - L'événement shopify:section:load
   */
  function handleSectionLoad(event) {
    const sectionElement = event.detail ? event.detail.container : event.target;
    if (!sectionElement) {
      return;
    }
    
    // Détruire les swipers existants dans cette section
    const existingSwipers = sectionElement.querySelectorAll(`${DEFAULT_SWIPER_SELECTOR}[data-swiper-initialized="true"]`);
    existingSwipers.forEach((swiperEl) => {
      destroySwiper(swiperEl);
    });
    
    // Initialiser immédiatement les swipers prioritaires dans cette section
    if (typeof Swiper !== 'undefined') {
      const prioritySwipers = sectionElement.querySelectorAll(
        `${DEFAULT_SWIPER_SELECTOR}[data-swiper-priority="immediate"]:not([data-swiper-initialized="true"])`
      );
      prioritySwipers.forEach((swiperEl) => {
        initSwiper(swiperEl);
      });
    }
    
    // Réinitialiser les autres swipers de cette section (différé)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        scanSections(sectionElement);
        // Réactiver l'observer si nécessaire (nouveau swiper ajouté)
        setupObserver();
      }, { timeout: 500 });
    } else {
      setTimeout(() => {
        scanSections(sectionElement);
        // Réactiver l'observer si nécessaire (nouveau swiper ajouté)
        setupObserver();
      }, 100);
    }
  }
  
  /**
   * Gère le réordonnancement des sections Shopify
   */
  function handleSectionReorder() {
    // Réinitialiser tous les swipers (les IDs peuvent avoir changé)
    swiperInstances.forEach((instance, swiperEl) => {
      destroySwiper(swiperEl);
    });
    
    // Rescanner après un court délai
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        scanSections();
        // Réactiver l'observer si nécessaire
        setupObserver();
      }, { timeout: 500 });
    } else {
      setTimeout(() => {
        scanSections();
        // Réactiver l'observer si nécessaire
        setupObserver();
      }, 100);
    }
  }
  
  /**
   * Initialisation principale
   */
  function init() {
    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Initialiser immédiatement les swipers prioritaires
        initPrioritySwipers();
        // Initialiser les autres swipers de manière différée (LCP-friendly)
        initWhenIdle();
        setupObserver();
      });
    } else {
      // DOM déjà chargé
      // Initialiser immédiatement les swipers prioritaires (ex: upsell)
      initPrioritySwipers();
      // Initialiser les autres swipers de manière différée (LCP-friendly)
      initWhenIdle();
      setupObserver();
    }
    
    // Fallback : réessayer après window.load si Swiper.js se charge après
    window.addEventListener('load', () => {
      if (typeof Swiper === 'undefined') {
        // Retry avec backoff exponentiel
        let retries = 0;
        const maxRetries = 5;
        const retryInterval = setInterval(() => {
          if (typeof Swiper !== 'undefined' || retries >= maxRetries) {
            clearInterval(retryInterval);
            if (typeof Swiper !== 'undefined') {
              scanSections();
            }
          }
          retries++;
        }, 500);
      } else {
        scanSections();
      }
    });
    
    // Réinitialisation après les événements Shopify (éditeur de thème)
    document.addEventListener('shopify:section:load', handleSectionLoad);
    document.addEventListener('shopify:section:reorder', handleSectionReorder);
  }
  
  // Démarrer l'initialisation
  init();
  
  // Exposer les fonctions globalement pour le debug et l'utilisation externe
  window.initCustomSwipers = scanSections;
  window.destroyCustomSwipers = function(container = document) {
    const swipers = container.querySelectorAll(`${DEFAULT_SWIPER_SELECTOR}[data-swiper-initialized="true"]`);
    swipers.forEach((swiperEl) => {
      destroySwiper(swiperEl);
    });
  };
})();
